import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, change, SubmissionError, submit } from 'redux-form';

import Button from '../../Button';
import Paragraph from '../../Paragraph';
import Heading from '../../Heading';
import { Row } from '../Scaffold';
import renderField from '../../Inputs';
import Progress from '../../Progress';

const aboveZero = (value) => {
  if (value < 0) {
    return 0;
  }
  return value;
};

const number = value => (value && isNaN(Number(value)) ? 'Must be a number' : undefined);

const parsers = {
  kgnumber: (value) => {
    let result;
    if (value.indexOf('.') > -1 && value.split('.')[1].length > 2) {
      result = value.substring(0, value.indexOf('.') + 3);
    } else if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = value.substring(0, 3);
    } else if (parseInt(value.split('.')[0], 10) === 0) {
      result = '';
    } else if (parseFloat(value) > 999.99) {
      result = '';
    }

    if (value) {
      return new RegExp(/^(?!0+$)\d{1,3}([.]\d{0,2})?$/g).test(value) ? value : result;
    }
    return '';
  },
  stnumber: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = parseInt(value.substring(0, 3), 10) <= 156 ? value.substring(0, 3) : '';
    }

    if (value) {
      return new RegExp(/^(15[0-6]|1[0-4][0-9]|[1-9]?[0-9])$/g).test(value) ? value : result;
    }
    return '';
  },
  lbnumber: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 4) {
      result = parseInt(value.substring(0, 4), 10) <= 2202 ? value.substring(0, 4) : '';
    }

    if (value) {
      return new RegExp(/^(220[0-2]|2[01][0-9][0-9]|1[0-9]{3}|[1-9]?[0-9]{0,2})$/g).test(value) ? value : result;
    }
    return '';
  },
  cmnumber: (value) => {
    let result;
    if (value.indexOf('.') > -1 && value.split('.')[1].length > 2) {
      result = value.substring(0, value.indexOf('.') + 3);
    } else if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = parseInt(value.substring(0, 3), 10) < 300 ? value.substring(0, 3) : '';
    } else if (parseInt(value.split('.')[0], 10) === 0) {
      result = '';
    } else if (parseFloat(value) > 299.99) {
      result = '';
    }

    if (value) {
      return new RegExp(/^([12]\d{2}|[1-9][0-9]?)([.]\d{0,2})?$$/g).test(value) ? value : result;
    }
    return '';
  },
  ftnumber: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 2) {
      result = parseInt(value.substring(0, 2), 10) <= 10 ? value.substring(0, 2) : '';
    }

    if (value) {
      return new RegExp(/^(10|[0-9])$/g).test(value) ? value : result;
    }
    return '';
  },
  innumber: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = parseInt(value.substring(0, 3), 10) <= 118 ? value.substring(0, 3) : '';
    }

    if (value) {
      return new RegExp(/^(11[0-8]|10[0-9]|[1-9]?[0-9])$/g).test(value) ? value : result;
    }
    return '';
  },
};

class WeightHeight extends React.PureComponent {
  state = {
    showkg: false,
    showcm: false,
    lb: 0,
    st: 0,
    ft: 0,
    in: 0,
  };

  componentDidMount() {
    if (window.pageYOffset > this.sectionRef.offsetTop) {
      window.scrollTo({ top: this.sectionRef.offsetTop, behavior: 'smooth' });
    }
  }

  weightChange = (e) => {
    const weights = {
      st: this.state.st,
      lb: this.state.lb,
    };

    weights.st = this.weightSt.value;
    this.setState({ st: this.weightSt.value });
    weights.lb = this.weightLb.value;
    this.setState({ lb: this.weightLb.value });

    if (e.target.name === 'weight_stones') {
      weights.st = e.target.value;
      this.setState({ st: e.target.value });
    } else if (e.target.name === 'weight_pounds') {
      weights.lb = e.target.value;
      this.setState({ lb: e.target.value });
    }

    weights.st = (weights.st) ? weights.st : 0;
    weights.lb = (weights.lb) ? weights.lb : 0;

    let kg = weights.st * 6.35;
    kg += weights.lb * 0.453;
    kg = kg.toFixed(2);

    this.props.dispatch(change('campaign', 'campaign[weight_weight]', kg));
  }

  heightChange = (e) => {
    const heights = {
      ft: this.state.ft,
      in: this.state.in,
    };

    heights.ft = this.heightFt.value;
    this.setState({ ft: this.heightFt.value });
    heights.in = this.heightIn.value;
    this.setState({ in: this.heightIn.value });

    if (e.target.name === 'height_ft') {
      heights.ft = e.target.value;
      this.setState({ ft: e.target.value });
    } else if (e.target.name === 'height_in') {
      heights.in = e.target.value;
      this.setState({ in: e.target.value });
    }

    heights.ft = (heights.ft) ? heights.ft : 0;
    heights.in = (heights.in) ? heights.in : 0;

    let cm = heights.ft * 30.48;
    cm += heights.in * 2.54;
    cm = cm.toFixed(2);

    this.props.dispatch(change('campaign', 'campaign[weight_height]', cm));
  }

  validate = (valueSet) => {
    const values = valueSet;
    const errors = { campaign: {} };
    let hasError = false;

    if (!values.campaign) values.campaign = {};

    if (!values.campaign.weight_height) {
      errors.campaign.weight_height = 'Please enter your height';
      hasError = true;
    }
    if (values.campaign.weight_height <= 0) {
      errors.campaign.weight_height = 'Your height must be greater than 0';
      hasError = true;
    }
    if (values.campaign.weight_height && values.campaign.weight_height.slice(-1) === '.') {
      errors.campaign.weight_height = 'Please enter a vaild number';
      hasError = true;
    }

    if (!values.campaign.weight_weight) {
      errors.campaign.weight_weight = 'Please enter your weight';
      hasError = true;
    }
    if (values.campaign.weight_weight <= 0) {
      errors.campaign.weight_weight = 'Your weight must be greater than 0';
      hasError = true;
    }
    if (values.campaign.weight_weight && values.campaign.weight_weight.slice(-1) === '.') {
      errors.campaign.weight_weight = 'Please enter a vaild number';
      hasError = true;
    }

    if (!values.weight_stones) {
      errors.weight_stones = 'Please enter your weight';
      hasError = true;
    }
    if (values.weight_stones <= 0) {
      errors.weight_stones = 'Your weight must be greater than 0';
      hasError = true;
    }
    if (!values.height_ft) {
      errors.height_ft = 'Please enter your height';
      hasError = true;
    }
    if (values.height_ft <= 0) {
      errors.height_ft = 'Your height must be greater than 0';
      hasError = true;
    }

    if (hasError) {
      throw new SubmissionError(errors);
    }

    this.props.dispatch(submit('campaign'));
  }

  render() {
    const { handleSubmit, submitting } = this.props;
    return (
      <Row classes="compaign-page lose-weight" setRef={(sectionRef) => { this.sectionRef = sectionRef; }}>
        <div className="campaign-bg purple">
          <svg width="800" height="800" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M-191.538 1736.18s948.537-475.5 1124.563-1274.192c176.025-798.691-159.68-1553.548-159.68-1553.548S-559.786 393.866-191.538 1736.18z" className="svg_bg_1" />
            <path d="M-453.085 1609.5s686.975 166.29 1120.563-167.95c433.592-334.23 585.632-865.687 585.632-865.687S-64.2127 762.802-453.085 1609.5z" className="svg_bg_2" />
            <path d="M-453.235 1609.39S150.648 1239.78 223.1 697.465C295.553 155.149 27.7053-326.192 27.7053-326.192S-775.018 737.63-453.235 1609.39z" className="svg_bg_3" />
            <path d="M706.354-437.052C250.513-42.8394-467.631 720.706-453.084 1608.95c23.479-11.88 950.315-485.4 1124.109-1273.962 59.894-271.7576 60.543-538.441 35.329-772.04z" className="svg_bg_4" />
          </svg>
        </div>
        <Progress step="loseWeight" />
        <Paragraph center>
          <img className="section-logo" src="/site/images/campaign/wh-icon.svg" alt="" width="50" height="50" />
        </Paragraph>

        <form onSubmit={handleSubmit(this.validate)} className="form-campaign">
          <Heading classes="header-text campaign-header-text" center>What’s your BMI?</Heading>
          <Paragraph classes="desc-text" center>Please enter your height and weight.</Paragraph>

          <div className="flex-inline">
            <div className="col-50">
              <Paragraph classes="section-text" center>your Height</Paragraph>
              <Paragraph classes="switch-button-container" center>
                <Button classes={['switch-button']} label={this.state.showcm ? 'Switch to imperial' : 'Switch to metric'} onClick={() => this.setState({ showcm: !this.state.showcm })} />
              </Paragraph>
              <div className="input-container">
                {this.state.showcm ? (
                  <Field
                    name="campaign[weight_height]"
                    type="text"
                    component={renderField}
                    onChange={(e, value) => {
                      const cm = parseInt(value, 10);
                      if (!cm) return;
                      let inc = (cm * 0.393700787).toFixed(0);
                      const ft = Math.floor(inc / 12);
                      inc %= 12;

                      this.props.dispatch(change('campaign', 'height_ft', ft));
                      this.props.dispatch(change('campaign', 'height_in', inc));
                    }}
                    normalize={aboveZero}
                    validate={[number]}
                    parse={parsers.cmnumber}
                    inputUnit="cm"
                    classes="text-input-field"
                    inputMode="numeric"
                  />
                ) : (
                  <div className="input-group">
                    <Field
                      ref={(heightFt) => {
                        this.heightFt = heightFt;
                      }}
                      name="height_ft"
                      type="text"
                      normalize={aboveZero}
                      validate={[number]}
                      parse={parsers.ftnumber}
                      component={renderField}
                      onChange={this.heightChange}
                      inputUnit="ft"
                      classes="text-input-field"
                      inputMode="numeric"
                    />

                    <Field
                      ref={(heightIn) => {
                        this.heightIn = heightIn;
                      }}
                      name="height_in"
                      type="text"
                      normalize={aboveZero}
                      validate={[number]}
                      parse={parsers.innumber}
                      component={renderField}
                      onChange={this.heightChange}
                      inputUnit="inches"
                      classes="text-input-field"
                      inputMode="numeric"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="col-50">
              <Paragraph classes="section-text" center>your Weight</Paragraph>
              <Paragraph classes="switch-button-container" center>
                <Button classes={['switch-button']} label={this.state.showkg ? 'Switch to imperial' : 'Switch to metric'} onClick={() => this.setState({ showkg: !this.state.showkg })} />
              </Paragraph>
              <div className="input-container">
                {this.state.showkg ? (
                  <Field
                    name="campaign[weight_weight]"
                    type="text"
                    component={renderField}
                    onChange={(e, kg) => {
                      let lb = (kg * 2.204623).toFixed(0);
                      const st = Math.floor(lb / 14);
                      lb %= 14;
                      this.props.dispatch(change('campaign', 'weight_stones', st));
                      this.props.dispatch(change('campaign', 'weight_pounds', lb));

                      this.setState({ lb, st });
                    }}
                    normalize={aboveZero}
                    validate={[number]}
                    parse={parsers.kgnumber}
                    inputUnit="kg"
                    classes="text-input-field"
                    inputMode="numeric"
                  />
                ) : (
                  <div className="input-group">
                    <Field
                      ref={(weightSt) => {
                        this.weightSt = weightSt;
                      }}
                      name="weight_stones"
                      type="text"
                      normalize={aboveZero}
                      validate={[number]}
                      parse={parsers.stnumber}
                      component={renderField}
                      onChange={this.weightChange}
                      classes="text-input-field"
                      inputUnit="stone"
                      inputMode="numeric"
                    />

                    <Field
                      ref={(weightLb) => {
                        this.weightLb = weightLb;
                      }}
                      name="weight_pounds"
                      type="text"
                      normalize={aboveZero}
                      validate={[number]}
                      parse={parsers.lbnumber}
                      component={renderField}
                      onChange={this.weightChange}
                      classes="text-input-field"
                      inputUnit="lbs"
                      inputMode="numeric"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <button className="button js-register-advance color-button purple" type="submit" disabled={submitting}>Next</button>
        </form>
      </Row>
    );
  }
}

WeightHeight.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default reduxForm({
  form: 'campaign',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(WeightHeight);



// WEBPACK FOOTER //
// ./src/Components/Campaign/FormPages/WeightHeight.jsx